<template>
  <div class="emailTemplatesPage">
    <h3 class="mb-4">Email Template Customization</h3>
    <div
      v-if="loadingTemplates"
      class="emailTemplatesPage"
      data-cy="loading-message"
    >
      Loading Email Templates...
    </div>
    <div v-else-if="errorLoading">Unexpected error. Please try again.</div>
    <div v-else-if="emailTemplates.length === 0" data-cy="no-templates-message">
      No email templates found. Please try again later.
    </div>
    <div v-else>
      <Select
        :model-value="internalSelectedId"
        :allow-empty="false"
        :disabled="loadingCustomizations"
        :options="templateOptions"
        label="Email Template"
        class="mb-4 emailTemplateSelector"
        data-cy="template-selector"
        @update:model-value="handleUpdateTemplateId"
      />
      <div v-if="selectedTemplateKey" class="templateContainer">
        <div class="templateViews grid-cols-1 md:grid-cols-2">
          <div>
            <div class="title">Editing View</div>
            <div v-if="templateCustomizations" class="emailTemplateContent">
              <div v-if="templateCustomizations.length === 0">
                No customizations found for this template.
              </div>
              <div
                v-for="(customization, index) in templateSpecificCustomizations"
                :key="customization.key"
                :data-cy="`customization-editor-${customization.key}`"
                :class="[`customization-editor-${customization.key}`, 'mb-3']"
              >
                <EmailTemplateEditor
                  :key="renderKey"
                  v-model="templateSpecificCustomizations[index]"
                  @reset-to-default="resetCustomizationToDefault"
                />
              </div>
              <div
                v-if="templateSpecificCustomizations.length"
                class="globalChangesDivider"
              ></div>
              <div v-if="globalCustomizations.length">
                <div
                  class="flex flex-col md:flex-row md:items-center globalChangesMessage mb-3"
                >
                  <h2 class="mr-2 mb-0">Global Changes</h2>
                  <p class="mb-0">(Any change here will be applied globally)</p>
                </div>
                <div
                  v-for="(customization, index) in globalCustomizations"
                  :key="customization.key"
                  :data-cy="`customization-editor-${customization.key}`"
                  :class="[`customization-editor-${customization.key}`, 'mb-3']"
                >
                  <EmailTemplateEditor
                    :key="renderKey"
                    v-model="globalCustomizations[index]"
                    hide-global-message
                    @reset-to-default="resetCustomizationToDefault"
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="title">Preview</div>
            <EmailTemplatePreview class="emailTemplatePreview" />
          </div>
        </div>
        <div v-if="templateCustomizations?.length" class="actions d-flex mt-3">
          <div class="flex items-center gap-4 justify-end w-full">
            <Button
              v-if="templateCustomizationsChanged"
              :saving="saving"
              class="mr-4"
              type="secondary"
              style="min-width: 80px"
              data-cy="cancel-changes"
              @click="handleCancelChanges"
            >
              <span>Cancel Changes</span>
            </Button>
            <Button
              :saving="saving"
              :disabled="saving"
              style="min-width: 80px"
              data-cy="save-changes"
              @click="handleSaveChanges"
            >
              <span>Save Changes</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import sortBy from "lodash/sortBy";
import timeoutPromise from "serviceshift-ui/shared/src/lib/timeoutPromise";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import EmailTemplateEditor from "@/EmailTemplates/EmailTemplateEditor.vue";
import EmailTemplatePreview from "@/EmailTemplates/EmailTemplatePreview.vue";
import { useEmailTemplates } from "@/EmailTemplates/useEmailTemplates";

const store = useStore();
const {
  resetCustomizationToDefault,
  revertCustomizations,
  templateCustomizationsChanged,
  selectedTemplateKey,
  loadingTemplates,
  emailTemplates,
  saveTemplateCustomizations,
  refreshEmailTemplates,
  templateCustomizations,
  loadingCustomizations
} = useEmailTemplates(true);
const errorLoading = ref(false);
const saving = ref(false);
const internalSelectedId = ref<string>();
const renderKey = ref(0);

const templateOptions = computed(() => {
  return sortBy(
    emailTemplates.value.map((template) => {
      return {
        label: template.description,
        value: template.key
      };
    }),
    (option) => option?.label?.toLowerCase()
  );
});

const templateSpecificCustomizations = computed(() => {
  return templateCustomizations.value.filter((customization) => {
    return customization.category === "Emails";
  });
});

const globalCustomizations = computed(() => {
  return templateCustomizations.value.filter((customization) => {
    return customization.category !== "Emails";
  });
});

async function revertContent() {
  renderKey.value = renderKey.value + 1;
  revertCustomizations();
}

async function handleCancelChanges() {
  await store.dispatch("confirmChanges", {
    title: "Cancel Changes",
    message: "Are you sure you want to cancel your content changes?"
  });
  revertContent();
}

function handleUpdateTemplateId(newTemplateId: string) {
  if (
    newTemplateId === internalSelectedId.value ||
    newTemplateId === selectedTemplateKey.value
  ) {
    return;
  }
  internalSelectedId.value = newTemplateId;
  const decisionToContinue = showConfirmUnsavedChanges();
  if (decisionToContinue) {
    selectedTemplateKey.value = newTemplateId;
    revertContent();
  }
}

function showConfirmUnsavedChanges() {
  let decisionToContinue = true;
  if (templateCustomizationsChanged.value) {
    decisionToContinue = confirm(
      "You have unsaved changes. Are you sure you want to change templates? "
    );
  }
  if (!decisionToContinue) {
    timeoutPromise().then(() => {
      internalSelectedId.value = selectedTemplateKey.value;
    });
    return false;
  }
  return true;
}

async function handleSaveChanges() {
  saving.value = true;
  try {
    await saveTemplateCustomizations();
    window.notify.success("Customizations saved.");
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  errorLoading.value = false;
  try {
    await refreshEmailTemplates();
    if (templateOptions.value.length) {
      selectedTemplateKey.value = templateOptions.value[0].value;
      internalSelectedId.value = selectedTemplateKey.value;
    }
  } catch (e) {
    if (e != "No email templates found.") {
      window.notify.error("Unable to load email templates at this time.");
      errorLoading.value = true;
    }
  }
});

defineExpose({
  hasUnsavedChanges: () => templateCustomizationsChanged.value,
  onBeforeTabChange: showConfirmUnsavedChanges
});
</script>

<style lang="scss" scoped>
.resetButton {
  background: none;
  border: none;
  color: var(--primary-color);
  outline: none;
  padding: 7.5px 15px;
  cursor: pointer;
  &:hover {
    background: var(--neutral-gray);
  }
}
.emailTemplateSelector {
  max-width: 550px;
}
.emailTemplatePreview {
  min-height: calc(100vh - 570px);
  height: calc(100% - 70px);
  overflow: auto;
  min-height: 500px;
  width: 100%;
  border: 1px solid #c7cfd3;
}
.templateContainer {
  background: #f9f9f9;
  padding: 24px;
}
.title {
  font-size: 18px;
  border-bottom: 1px solid #c7cfd3;
  padding-bottom: 8px;
  margin-bottom: 16px;
  font-weight: 600;
}
.templateViews {
  display: grid;
  grid-template-columns: calc(50% - 18px) calc(50% - 18px);
  gap: 36px;
}

.globalChangesDivider {
  margin-top: 30px;
  padding-top: 15px;
  padding-bottom: 15px;
  border-top: 1px solid #c7cfd3;
}
.globalChangesMessage {
  h2 {
    font-size: 16px;
  }
  p {
    font-size: 12px;
  }
}
.customization-editor-dark_logo_url,
.customization-editor-logo_size_percentage {
  flex: 1;
  display: inline-flex;
}
.customization-editor-dark_logo_url {
  margin-right: 15px;
}
</style>
